import { useEffect, useState } from 'react'

import { withContentSource } from '../Utils/url-helpers'

import { ProductListSections, TravelSearchConfigResponse } from '../../api/travel-search-config'
import { getBaseApi, getLocaleFromUrlOrQuery, getUrlParts, isPreviewUrl } from '../Utils/common'
import { fetcher } from '../Utils/fetcher'

const baseApi = getBaseApi()

export function useTravelSearchConfig(): UseTravelSearchConfigResult {
  const [configData, setConfigData] = useState<TravelSearchConfigResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const previewId = isPreviewUrl()
  const parts = getUrlParts()
  const locale = getLocaleFromUrlOrQuery()
  let urlSlug = parts.slice(1).join('/') || '/'
  let isNextApplication = false

  // Handle localhost and non-prod
  if (window.location.hostname !== 'www.dfds.com' && window.location.pathname.endsWith('/travel')) {
    const parts = window.location.pathname.split('/')
    urlSlug = parts[2]
    isNextApplication = true
  } else {
    isNextApplication = document.getElementById('__next') !== null
  }

  if (window.location.hostname === 'localhost') {
    isNextApplication = true
  }

  const params = withContentSource(new URLSearchParams({ isNextApplication: String(isNextApplication), locale, urlSlug }))
  if (previewId) params.set('previewId', previewId)
  const url = `${baseApi}/travel-search-config?${params}`

  useEffect(() => {
    const controller = new AbortController()
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true)

    fetcher<TravelSearchConfigResponse>(url, {
      cache: 'default',
      signal: controller.signal,
    })
      .then((res) => {
        setConfigData(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to fetch travel search config:', err)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [url])

  const legacyConfig = configData?.source === 'legacy' && 'availableMinibooks' in configData ? configData : null
  const sharedConfig = configData?.source === 'shared' && 'travelSearchId' in configData ? configData : null

  return {
    availableMinibooks: legacyConfig?.availableMinibooks ?? [],
    isLoading,
    isMultiOffer: legacyConfig?.isMultiOffer ?? false,
    optionalOfferTitle: legacyConfig?.optionalOfferTitle,
    route: legacyConfig?.route,
    source: configData?.source,
    travelSearchId: sharedConfig?.travelSearchId,
  }
}

export interface UseTravelSearchConfigResult {
  availableMinibooks: ProductListSections[] | string
  isLoading: boolean
  isMultiOffer: boolean
  optionalOfferTitle?: string
  route?: string
  source?: 'legacy' | 'shared'
  travelSearchId?: string
}
